export const GROWTH_EVENT_NAMES = Object.freeze({
  TOOL_CALCULATE: "tool_calculate",
  TOOL_CTA_CLICK: "tool_cta_click",
  CONTENT_CTA_CLICK: "content_cta_click",
  BLOG_PRODUCT_LINK_CLICK: "blog_product_link_click",
  CHAT_BRIDGE_CONSULTATION_OPEN: "chat_bridge_consultation_open",
  CHAT_BRIDGE_DRAFT_OPEN: "chat_bridge_draft_open",
  FORM_START: "form_start",
  GENERATE_LEAD: "generate_lead",
  SIGN_UP: "sign_up",
} as const);

export type GrowthEventName =
  (typeof GROWTH_EVENT_NAMES)[keyof typeof GROWTH_EVENT_NAMES];

export type GrowthBrand = "ikai" | "gaiai";

export type GrowthSiteContext = {
  brand: GrowthBrand;
  site_host: "ikai.com.tr" | "gaiai.ai";
};

type AttributionParams = {
  source_path?: string;
  tool_slug?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
};

type GrowthEventPayloads = {
  tool_calculate: AttributionParams & {
    source_path: string;
    tool_slug: string;
  };
  tool_cta_click: AttributionParams & {
    source_path: string;
    tool_slug: string;
    cta_target: string;
  };
  content_cta_click: AttributionParams & {
    source_path: string;
    content_type: "blog" | "money_page";
    content_slug: string;
    cta_id: string;
    cta_target: string;
    interaction_status: "intent_only";
  };
  blog_product_link_click: {
    source_path: string;
    target_path: string;
    interaction_status: "navigation_only";
  };
  chat_bridge_consultation_open: AttributionParams & {
    source_path: string;
    interaction_status: "intent_only";
  };
  chat_bridge_draft_open: AttributionParams & {
    source_path: string;
    draft_channel: "whatsapp" | "email";
    interaction_status: "draft_only";
  };
  form_start: AttributionParams & {
    conversion_event_id: string;
    form_name: "assistant_lead" | "register";
    method: "lead_form" | "email" | "google";
  };
  generate_lead: AttributionParams & {
    conversion_event_id: string;
    form_name: "assistant_lead";
    method: "lead_form";
  };
  sign_up: AttributionParams & {
    conversion_event_id: string;
    form_name: "register";
    method: "email" | "google";
  };
};

type GrowthDataLayer = Array<IArguments | Record<string, unknown>>;
type GrowthWindow = Window & {
  dataLayer?: GrowthDataLayer;
  gtag?: (..._args: unknown[]) => void;
  __IKAI_GROWTH_ANALYTICS_ENABLED__?: boolean;
  __ASANMOD_GROWTH_SITE_CONTEXT__?: GrowthSiteContext;
};

const FORBIDDEN_PII_KEY =
  /(^|_)(email|e_mail|phone|telephone|full_name|first_name|last_name|address|password)(_|$)/i;
const EMAIL_LIKE_VALUE = /\b[^\s@]+@[^\s@]+\.[^\s@]+\b/i;
const EVENT_SCHEMA_VERSION = "growth.v2";

const SITE_CONTEXT_BY_HOST = Object.freeze<Record<string, GrowthSiteContext>>({
  "ikai.com.tr": { brand: "ikai", site_host: "ikai.com.tr" },
  "www.ikai.com.tr": { brand: "ikai", site_host: "ikai.com.tr" },
  "gaiai.ai": { brand: "gaiai", site_host: "gaiai.ai" },
  "www.gaiai.ai": { brand: "gaiai", site_host: "gaiai.ai" },
});

function growthWindow(): GrowthWindow | null {
  return typeof window === "undefined" ? null : (window as GrowthWindow);
}

function assertPiiFree(params: Record<string, unknown>): void {
  const forbiddenKey = Object.keys(params).find((key) =>
    FORBIDDEN_PII_KEY.test(key)
  );
  if (forbiddenKey) {
    throw new Error(`GROWTH_ANALYTICS_PII_KEY_BLOCKED:${forbiddenKey}`);
  }
  const forbiddenValue = Object.entries(params).find(
    ([, value]) =>
      typeof value === "string" &&
      (EMAIL_LIKE_VALUE.test(value) || isPhoneLikeValue(value))
  );
  if (forbiddenValue) {
    throw new Error(`GROWTH_ANALYTICS_PII_VALUE_BLOCKED:${forbiddenValue[0]}`);
  }
}

function isPhoneLikeValue(value: string): boolean {
  const normalized = value.trim();
  if (!/^\+?[\d\s().-]+$/.test(normalized)) return false;
  const digitCount = normalized.replace(/\D/g, "").length;
  return digitCount >= 10 && digitCount <= 15;
}

function compactParams(
  params: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );
}

export function resolveGrowthSiteContext(
  hostname: string
): GrowthSiteContext | null {
  const normalizedHost = hostname
    .trim()
    .toLocaleLowerCase("en-US")
    .replace(/\.$/, "");
  return SITE_CONTEXT_BY_HOST[normalizedHost] || null;
}

export function getGrowthSiteContext(): GrowthSiteContext | null {
  return growthWindow()?.__ASANMOD_GROWTH_SITE_CONTEXT__ || null;
}

export function configureGrowthAnalytics(
  enabled: boolean,
  siteContext: GrowthSiteContext | null = null
): void {
  const target = growthWindow();
  if (!target) return;
  if (!enabled) {
    target.__IKAI_GROWTH_ANALYTICS_ENABLED__ = false;
    delete target.__ASANMOD_GROWTH_SITE_CONTEXT__;
    return;
  }
  target.__ASANMOD_GROWTH_SITE_CONTEXT__ = siteContext || undefined;
  target.__IKAI_GROWTH_ANALYTICS_ENABLED__ = Boolean(siteContext);
  if (siteContext) target.dataLayer = target.dataLayer || [];
}

export function createConversionEventId(_scope: "lead" | "signup"): string {
  return typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (token) => {
        const random = Math.floor(Math.random() * 16);
        const value = token === "x" ? random : (random & 0x3) | 0x8;
        return value.toString(16);
      });
}

export function trackGrowthEvent<Name extends GrowthEventName>(
  eventName: Name,
  payload: GrowthEventPayloads[Name]
): boolean {
  const target = growthWindow();
  const siteContext = getGrowthSiteContext();
  if (!target?.__IKAI_GROWTH_ANALYTICS_ENABLED__ || !siteContext) return false;

  const params = compactParams({
    ...payload,
    ...siteContext,
    event_schema_version: EVENT_SCHEMA_VERSION,
  });
  assertPiiFree(params);

  if (typeof target.gtag === "function") {
    target.gtag("event", eventName, params);
    return true;
  }

  target.dataLayer = target.dataLayer || [];
  (function queueGtagCommand(...args: unknown[]) {
    void args;
    target.dataLayer?.push(arguments);
  })("event", eventName, params);
  return true;
}
